import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { FormattedMessage } from 'react-intl';
import { BulkUpdateValidationFeedback } from '../../../sharedComponents/bulkStatusUpdate/BulkUpdateValidationFeedback';
import { BulkStatusError } from '../../../sharedComponents/bulkStatusUpdate/BulkStatusErrorMessage';
import { ServiceOrderBulkUpdatePreviewErrorCode, ServiceOrderBulkUpdatePreviewResult } from '../serviceOrder.types';
import { BulkUpdateMaxSizeErrorMessage } from '../../../sharedComponents/bulkStatusUpdate/BulkUpdateMaxSizeErrorMessage';
import { SERVICE_ORDER_MAX_SELECTION_SIZE } from './ServiceOrderBulkSelectionDropdown';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import { useState } from 'react';
import {
    SERVICE_BULK_UPDATE_FORM_ID,
    ServiceOrderBulkUpdateFormState,
    ServiceOrderBulkUpdateFormStep,
} from './ServiceOrderBulkUpdateFormStep';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { getServiceOrderBulkSelection, serviceOrdersSlice } from '../../../reducers/serviceOrdersSlice';
import { DateParseErrors, initialDateParseErrors, ServiceOrderDateField } from '../shared/ServiceOrderFormValidation';
import { runInBackgroundCallback } from '../../../../../configuration/setup/backgroundActions';
import { useSaveServiceOrderExecution } from '../shared/useSaveServiceOrderExecution';

type ServiceOrderBulkUpdateDialogProps = {
    readonly show: boolean;
    readonly previewResult: ServiceOrderBulkUpdatePreviewResult;
    readonly onClose: () => void;
};

enum ServiceOrderBulkUpdateDialogStep {
    VALIDATION = 'validation',
    FORM = 'form',
}
const initialDialogStep = ServiceOrderBulkUpdateDialogStep.VALIDATION;

const UpdateButton = ({ formHasParseError, isLoading }: { formHasParseError: boolean; isLoading: boolean }) => (
    <Button
        type="submit"
        form={SERVICE_BULK_UPDATE_FORM_ID}
        disabled={formHasParseError || isLoading}
        className={isLoading ? 'btn-loading' : ''}
        iconName={isLoading ? '' : 'rioglyph rioglyph-ok'}
    >
        <FormattedMessage id="outboundOrderBook.common.button.update" />
    </Button>
);

const CloseButton = ({ onClick }: { onClick: () => void }) => (
    <Button onClick={onClick}>
        <FormattedMessage id="outboundOrderBook.common.button.cancel" />
    </Button>
);

// eslint-disable-next-line max-lines-per-function
export const ServiceOrderBulkUpdateDialog = ({ show, previewResult, onClose }: ServiceOrderBulkUpdateDialogProps) => {
    const dispatch = useAppDispatch();
    const selectedIds = useAppSelector(getServiceOrderBulkSelection);
    const { saveExecution, isLoading } = useSaveServiceOrderExecution();

    const [currentStep, setCurrentStep] = useState(initialDialogStep);
    const [dateParseErrors, setDateParseErrors] = useState<DateParseErrors>(initialDateParseErrors);
    const setDateParseError = (field: ServiceOrderDateField, hasParseError: boolean) => {
        setDateParseErrors((current) => ({ ...current, [field]: hasParseError }));
    };
    const isValidationStep = currentStep === ServiceOrderBulkUpdateDialogStep.VALIDATION;
    const isSuccess = previewResult.success;
    const validOrders = isSuccess ? previewResult.serviceOrders : [];
    const validOrdersCount = isSuccess ? previewResult.serviceOrders.length : 0;
    const invalidOrders: BulkStatusError<ServiceOrderBulkUpdatePreviewErrorCode>[] = isSuccess
        ? previewResult.invalidOrders.map((error) => ({
              errorCode: error.errorCode,
              identifiers: error.orderIds,
              identifierType: 'ORDER',
          }))
        : [];
    const nextButtonDisabled = !isSuccess || validOrdersCount === 0;

    const requestError =
        !isSuccess && previewResult.errorCode === 'size-exceeded' ? (
            <BulkUpdateMaxSizeErrorMessage
                identifierType="ORDER"
                maxBulkSize={SERVICE_ORDER_MAX_SELECTION_SIZE}
                totalCount={selectedIds.length}
            />
        ) : undefined;

    const handleOnSave = runInBackgroundCallback(async (formState: ServiceOrderBulkUpdateFormState) => {
        const wasSaved = await saveExecution({
            orderIds: validOrders.map((order) => order.orderId),
            startedAt: formState.startedAt,
            completedAt: formState.completedAt,
            generalInformation: formState.generalInformation,
        });

        if (wasSaved) {
            dispatch(serviceOrdersSlice.actions.clearBulkSelection());
            handleClose();
        }
    });

    const handleClose = () => {
        onClose();
        setCurrentStep(initialDialogStep);
        setDateParseErrors(initialDateParseErrors);
    };

    return (
        <Dialog show={show} onClose={handleClose} bsSize={Dialog.SIZE_LG}>
            <Dialog.Title title={<FormattedMessage id="outboundOrderBook.common.bulkUpdate.dialogTitle" />} />
            <Dialog.Body>
                {isValidationStep ? (
                    <BulkUpdateValidationFeedback
                        validItemsCount={validOrdersCount}
                        invalidItems={invalidOrders}
                        requestError={requestError}
                    />
                ) : (
                    isSuccess && (
                        <ServiceOrderBulkUpdateFormStep
                            validOrders={previewResult.serviceOrders}
                            setDateParseError={setDateParseError}
                            isDisabled={isLoading}
                            handleOnSave={handleOnSave}
                        />
                    )
                )}
            </Dialog.Body>
            <Dialog.Footer>
                <ButtonToolbar>
                    <CloseButton onClick={handleClose} />
                    {isValidationStep ? (
                        <Button
                            bsStyle={Button.PRIMARY}
                            onClick={() => setCurrentStep(ServiceOrderBulkUpdateDialogStep.FORM)}
                            disabled={nextButtonDisabled}
                        >
                            <FormattedMessage id="outboundOrderBook.common.button.next" />
                        </Button>
                    ) : (
                        <UpdateButton
                            formHasParseError={Object.values(dateParseErrors).some(Boolean)}
                            isLoading={isLoading}
                        />
                    )}
                </ButtonToolbar>
            </Dialog.Footer>
        </Dialog>
    );
};
